import React from 'react';
import { Check, Smartphone, Monitor } from 'lucide-react';
import { SECTIONS } from '@/constants/testIds';
import { IMG } from '@/lib/assets';

const steps = [
  { n: 1, t: 'El animal se acerca', d: 'El animal se aproxima e introduce la cabeza en el comedero.' },
  { n: 2, t: 'Identificación RFID', d: 'La antena lee automáticamente la identificación individual del animal.' },
  { n: 3, t: 'Alimentación libre', d: 'El animal consume del alimento disponible en el bin de manera natural.' },
  { n: 4, t: 'Medición de consumo', d: 'Las celdas de carga miden la cantidad consumida y registran frecuencia y hora de cada toma.' },
  { n: 5, t: 'Datos en la plataforma', d: 'ID, ingesta y comportamiento se envían vía LoRa a la plataforma digital de gestión.' },
];

const capabilities = [
  'Identificar automáticamente cada animal.',
  'Registrar el alimento consumido por individuo.',
  'Medir la frecuencia y comportamiento de alimentación.',
  'Calcular indicadores de conversión alimenticia.',
  'Generar reportes automáticos para detectar los animales más eficientes — o aquellos con posibles anomalías o ineficiencias.',
];

export default function Tecnologia() {
  return (
    <section id="tecnologia" data-testid={SECTIONS.tecnologia} className="bg-[#F5EFE0] py-20 lg:py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">

        {/* Section intro quote */}
        <div className="mb-14 text-center max-w-4xl mx-auto">
          <span className="gold-divider font-cinzel text-[#A88340] text-xs tracking-[0.3em]">INNOVACIÓN Y TECNOLOGÍA</span>
          <p className="font-cormorant italic text-[#0F3527] text-xl lg:text-2xl mt-6 leading-snug">
            "Cada kilogramo de alimento tiene un costo. Nuestro objetivo es identificar qué animales
            convierten mejor ese alimento en producción, para que los productores inviertan donde
            realmente se genera valor."
          </p>
        </div>

        {/* Main Nexara block */}
        <div className="bg-[#0F3527] text-[#F5EFE0] rounded-sm overflow-hidden relative tc-grain mb-12">
          <div className="grid lg:grid-cols-2 gap-0 items-center">
            <div className="p-10 lg:p-14">
              <p className="font-cinzel text-[#C9A361] text-[11px] tracking-[0.3em] mb-6">PROYECTO COMEDERO INTELIGENTE</p>

              {/* Nexara logo on white plate */}
              <div className="bg-white rounded-sm inline-block px-7 py-5 mb-8 shadow-lg">
                <img
                  src={IMG.nexaraLogo}
                  alt="Nexara — From Feed to Performance"
                  className="h-20 lg:h-24 w-auto block"
                />
              </div>

              <p className="text-[#F5EFE0]/85 text-base lg:text-lg leading-relaxed mb-8">
                Sistema inteligente que <strong className="text-[#F5EFE0]">mide el consumo individual</strong> de
                cada animal y calcula su <strong className="text-[#F5EFE0]">tasa de conversión alimenticia</strong>.
                Identifica a cada animal vía RFID, registra ingesta, frecuencia y comportamiento, y envía los datos
                en tiempo real a una plataforma digital de gestión.
              </p>
              <p className="font-cormorant italic text-lg lg:text-xl text-[#F5EFE0]/95 border-l-2 border-[#C9A361] pl-5">
                Transformamos datos en decisiones para acelerar el mejoramiento genético y
                aumentar la eficiencia productiva.
              </p>
            </div>

            <div className="bg-white p-6 lg:p-8 m-6 lg:m-8 rounded-sm">
              <img
                src={IMG.nexaraFeeder}
                alt="Comedero Inteligente Nexara"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* What the system enables */}
        <div className="mb-14 grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-2">
            <span className="font-cinzel text-[#A88340] text-xs tracking-[0.3em]">EL SISTEMA PERMITE</span>
            <h3 className="font-cormorant text-[#0F3527] text-3xl lg:text-4xl mt-3 mb-5 leading-tight">
              Decisiones basadas en datos objetivos
            </h3>
            <div className="flex items-center gap-3 text-[#0F3527]/80 text-sm">
              <span className="inline-flex items-center gap-1.5">
                <Monitor size={16} className="text-[#A88340]" /> Computadora
              </span>
              <span className="text-[#C9A361]">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Smartphone size={16} className="text-[#A88340]" /> Dispositivos móviles
              </span>
            </div>
            <p className="text-[#0F3527]/75 text-sm leading-relaxed mt-3">
              Toda la información se almacena en una plataforma digital accesible desde cualquier
              lugar.
            </p>
          </div>
          <div className="lg:col-span-3 space-y-3">
            {capabilities.map((c, i) => (
              <div key={i} className="flex items-start gap-3 bg-white/70 border border-[#0F3527]/10 rounded-sm p-4">
                <span className="check-mark mt-0.5"><Check size={13} strokeWidth={3} /></span>
                <p className="text-[#0F3527] text-[15px] leading-relaxed">{c}</p>
              </div>
            ))}
          </div>
        </div>

        {/* How it works flow */}
        <div>
          <div className="text-center mb-10">
            <span className="font-cinzel text-[#A88340] text-xs tracking-[0.3em]">CÓMO FUNCIONA</span>
            <h3 className="font-cormorant text-[#0F3527] text-3xl lg:text-4xl mt-3">Un proceso automatizado de extremo a extremo</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {steps.map((s, i) => (
              <div key={s.n} className="relative bg-white/70 rounded-sm border border-[#0F3527]/10 p-5 hover:shadow-lg transition-shadow">
                <div className="w-9 h-9 rounded-full bg-[#0F3527] text-[#C9A361] font-cinzel text-sm flex items-center justify-center mb-3">
                  {s.n}
                </div>
                <h4 className="font-cinzel text-[12px] tracking-[0.18em] text-[#0F3527] mb-2 uppercase">{s.t}</h4>
                <p className="text-[#0F3527]/75 text-[13.5px] leading-relaxed">{s.d}</p>
                {i < steps.length - 1 && (
                  <span className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 text-[#C9A361] text-xl">›</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
